import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";
import { formatApiError } from "@/lib/api";
import { Building2 } from "lucide-react";

const COMPANY_TYPES = [
  "Security Company", "Event Organiser", "Close Protection Operator",
  "Asset Protection", "Property Management", "Corporate Client", "Other",
];

export default function RegisterCompany() {
  const { registerCompany } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [f, setF] = useState({
    email: "", password: "", company_name: "", contact_name: "",
    phone: "", company_type: COMPANY_TYPES[0], location: "", website: "",
    reason: "", agreement_accepted: false, referral_code: "",
  });

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) setF((cur) => ({ ...cur, referral_code: ref.toUpperCase() }));
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.agreement_accepted) return setError("You must agree to the platform terms.");
    setBusy(true);
    try {
      await registerCompany(f);
      nav("/company");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <div className="text-center mb-6">
        <div className="inline-flex w-14 h-14 rounded-md bg-brand items-center justify-center mb-4">
          <Building2 className="w-7 h-7 text-white" strokeWidth={2.2} />
        </div>
        <h1 className="text-3xl font-outfit font-bold text-white">Register as a Company</h1>
        <p className="text-white/60 text-sm mt-1 font-manrope">Get your operational account. Admin verification required.</p>
      </div>

      {/* Launch offer — free sign-up until Jan 2027 */}
      <div className="rounded-xl border-2 border-gold-400 bg-gradient-to-r from-gold-50 to-white p-4 mb-6 flex items-start gap-3" data-testid="reg-co-promo">
        <div className="w-10 h-10 rounded-md bg-gold-500 text-white font-outfit font-bold flex items-center justify-center flex-shrink-0">€0</div>
        <div className="text-sm font-manrope text-white/85 leading-relaxed">
          <span className="inline-block text-[10px] uppercase tracking-widest text-gold-700 font-outfit font-bold bg-gold-100 border border-gold-300 rounded-full px-2 py-0.5 mb-1">🎉 Launch offer</span>
          <div>
            <strong className="text-white">Free sign-up until 1st January 2027.</strong> No registration fee, no subscription, no credit card. You only pay the transparent platform fee when you book someone.
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="card-flat p-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>Company name</Label><Input required value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} data-testid="reg-co-name" /></div>
          <div><Label>Contact name</Label><Input required value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} data-testid="reg-co-contact" /></div>
          <div><Label>Email</Label><Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="reg-co-email" /></div>
          <div><Label>Password</Label><Input required type="password" minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="reg-co-password" /></div>
          <div><Label>Phone</Label><Input required value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="reg-co-phone" /></div>
          <div>
            <Label>Company type</Label>
            <Select value={f.company_type} onValueChange={(v) => setF({ ...f, company_type: v })}>
              <SelectTrigger data-testid="reg-co-type"><SelectValue /></SelectTrigger>
              <SelectContent>{COMPANY_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Location</Label><Input required value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="reg-co-location" /></div>
          <div><Label>Website (optional)</Label><Input value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} data-testid="reg-co-website" /></div>
        </div>

        <div>
          <Label>Reason for joining</Label>
          <Textarea rows={4} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="reg-co-reason" placeholder="Briefly tell us how you plan to use SecuSource." />
        </div>

        <label className="flex items-start gap-3 text-sm font-manrope">
          <Checkbox checked={f.agreement_accepted} onCheckedChange={(v) => setF({ ...f, agreement_accepted: !!v })} data-testid="reg-co-agreement" />
          <span>I agree to the SecuSource platform terms and confirm this information is accurate.</span>
        </label>

        <div>
          <Label>Referral code (optional)</Label>
          <Input
            value={f.referral_code}
            onChange={(e) => setF({ ...f, referral_code: e.target.value.toUpperCase() })}
            placeholder="e.g., K7X4QP"
            className="uppercase"
            data-testid="reg-co-referral"
          />
          <p className="text-xs text-white/60 mt-1 font-manrope">Referred by another SecuSource company? Enter their code — they get a €50 fee credit when you&apos;re approved.</p>
        </div>

        {error && <div className="text-sm text-red-600 font-manrope">{error}</div>}

        <Button type="submit" disabled={busy} className="w-full bg-brand hover:bg-brand-hover text-white py-6 font-manrope font-semibold" data-testid={AUTH.registerCompanySubmit}>
          {busy ? "Submitting…" : "Register Company"}
        </Button>

        <div className="text-center text-sm text-white/70 font-manrope">
          Already have an account? <Link to="/login" className="text-brand font-semibold">Login</Link>
        </div>
      </form>
    </div>
  );
}
